(() => {
  const SECTION_SELECTOR = '[data-metaobject-faqs-section]';
  const INITIALIZED_ATTRIBUTE = 'data-metaobject-faqs-initialized';

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const safeUrl = (value = '') => {
    const url = String(value).trim();

    if (/^(https?:|mailto:|tel:)/i.test(url) || url.startsWith('/') || url.startsWith('#')) {
      return url;
    }

    return '#';
  };

  const slugify = (value = '') => String(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'faq';

  const fieldMap = (fields = []) => Object.fromEntries(fields.map((field) => [field.key, field]));

  const parseRichText = (value) => {
    try {
      return JSON.parse(value || '{}');
    } catch (_error) {
      return null;
    }
  };

  const renderRichTextNode = (node) => {
    if (!node || typeof node !== 'object') return '';

    const children = Array.isArray(node.children) ? node.children.map(renderRichTextNode).join('') : '';

    if (node.type === 'root') return children;

    if (node.type === 'text') {
      let content = escapeHtml(node.value || '').replaceAll('\n', '<br>');
      const marks = Array.isArray(node.marks) ? node.marks.map((mark) => mark.type || mark) : [];

      if (node.bold || marks.includes('bold')) content = `<strong>${content}</strong>`;
      if (node.italic || marks.includes('italic')) content = `<em>${content}</em>`;
      if (node.underline || marks.includes('underline')) content = `<u>${content}</u>`;
      if (node.strikethrough || marks.includes('strikethrough')) content = `<s>${content}</s>`;

      return content;
    }

    if (node.type === 'paragraph') return `<p>${children}</p>`;
    if (node.type === 'line-break' || node.type === 'break') return '<br>';

    if (node.type === 'heading') {
      const level = Math.min(Math.max(Number(node.level) || 3, 2), 6);
      return `<h${level}>${children}</h${level}>`;
    }

    if (node.type === 'link') {
      const href = safeUrl(node.url || node.href);
      const newTab = node.target === '_blank' || node.newTab === true;
      return `<a href="${escapeHtml(href)}"${newTab ? ' target="_blank" rel="noopener noreferrer"' : ''}>${children}</a>`;
    }

    if (node.type === 'list' || node.type === 'ordered-list' || node.type === 'unordered-list') {
      const tag = node.type === 'ordered-list' || node.listType === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${children}</${tag}>`;
    }

    if (node.type === 'list-item') return `<li>${children}</li>`;

    return children;
  };

  const richTextToHtml = (value) => {
    const document = parseRichText(value);
    return document ? renderRichTextNode(document) : '';
  };

  const richTextToPlainText = (value) => {
    const document = parseRichText(value);

    const readNode = (node) => {
      if (!node || typeof node !== 'object') return '';
      if (node.type === 'text') return node.value || '';
      if (node.type === 'line-break' || node.type === 'break') return '\n';

      const content = Array.isArray(node.children) ? node.children.map(readNode).join('') : '';
      return ['paragraph', 'heading', 'list-item'].includes(node.type) ? `${content}\n` : content;
    };

    return document ? readNode(document).replace(/\s+/g, ' ').trim() : '';
  };

  const normalizeMetaobject = (node, config) => {
    const fields = fieldMap(node.fields);
    const categoryReference = fields.categories?.reference;
    const categoryFields = categoryReference?.__typename === 'Metaobject' ? fieldMap(categoryReference.fields) : {};
    const productHandles = (fields.applicable_items?.references?.nodes || [])
      .filter((reference) => reference.__typename === 'Product')
      .map((product) => product.handle)
      .filter(Boolean);
    const sortOrder = Number.parseFloat(fields.sort_order?.value);
    const categoryOrder = Number.parseFloat(categoryFields.order?.value);

    return {
      handle: node.handle,
      question: fields.question?.value?.trim() || '',
      answer: fields.answer?.value || '',
      answerHtml: richTextToHtml(fields.answer?.value),
      answerText: richTextToPlainText(fields.answer?.value),
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 999999,
      productHandles,
      category: categoryFields.title?.value?.trim() || '',
      categoryOrder: Number.isFinite(categoryOrder) ? categoryOrder : 999999,
      detailUrl: node.onlineStoreUrl || `${config.detailUrlBase}${node.handle}`,
    };
  };

  const FAQ_QUERY = `
    query MetaobjectFaqs($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        nodes {
          handle
          onlineStoreUrl
          fields {
            key
            value
            reference {
              __typename
              ... on Metaobject {
                handle
                type
                fields {
                  key
                  value
                }
              }
            }
            references(first: 50) {
              nodes {
                __typename
                ... on Product {
                  handle
                }
                ... on Metaobject {
                  handle
                  type
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const fetchFaqs = async (config) => {
    const endpoint = `${window.location.origin}/api/${config.apiVersion}/graphql.json`;
    const nodes = [];
    let after = null;
    let hasNextPage = true;
    let pageCount = 0;

    while (hasNextPage && pageCount < 100) {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontToken,
        },
        body: JSON.stringify({
          query: FAQ_QUERY,
          variables: {
            type: config.metaobjectType,
            first: 50,
            after,
          },
        }),
      });

      if (!response.ok) throw new Error(`Storefront API request failed (${response.status})`);

      const payload = await response.json();

      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));

      const connection = payload.data?.metaobjects;
      if (!connection) throw new Error('Storefront API returned no FAQ connection');

      nodes.push(...connection.nodes);
      hasNextPage = connection.pageInfo.hasNextPage;
      after = connection.pageInfo.endCursor;
      pageCount += 1;
    }

    if (hasNextPage) throw new Error('Storefront API FAQ pagination exceeded its safety limit');

    return nodes;
  };

  const accordionMarkup = (faq, detailLinkText, showDetailLink) => {
    const detailLink = showDetailLink
      ? `<a class="link mt1" href="${escapeHtml(safeUrl(faq.detailUrl))}">${escapeHtml(detailLinkText)}</a>`
      : '';

    return `
      <accordion-disclosure class="accordion">
        <details class="accordion__disclosure group" aria-expanded="false">
          <summary>
            <span class="accordion__toggle text-lg">
              <span>${escapeHtml(faq.question)}</span>
              <span class="animated-plus group-expanded:rotate" aria-hidden="true"></span>
            </span>
          </summary>
          <div class="accordion__content">
            <div class="prose">
              <div class="metafield-rich_text_field">${faq.answerHtml}</div>
              ${detailLink}
            </div>
          </div>
        </details>
      </accordion-disclosure>
    `;
  };

  const renderFaqs = (faqs, config) => {
    const categories = [...new Map(
      faqs
        .filter((faq) => faq.category)
        .map((faq) => [faq.category, { title: faq.category, order: faq.categoryOrder }]),
    ).values()].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    const showCategories = Boolean(config.showCategories && categories.length > 1);

    if (!showCategories) {
      return `<div class="faq__content">${faqs.map((faq) => accordionMarkup(faq, config.detailLinkText, false)).join('')}</div>`;
    }

    const categoryIds = new Map(categories.map((category, index) => [
      category.title,
      `block-${slugify(config.sectionId)}-${slugify(category.title)}-${index + 1}`,
    ]));
    const toc = categories.map((category, index) => `
      <a class="faq__toc-item${index === 0 ? ' is-active' : ''}" href="#${categoryIds.get(category.title)}">${escapeHtml(category.title)}</a>
    `).join('');
    const content = categories.map((category, index) => {
      const categoryFaqs = faqs.filter((faq) => faq.category === category.title);

      return `
        <div id="${categoryIds.get(category.title)}">
          <h2 class="subtitle h6 mb1${index === 0 ? '' : ' mt6'}">${escapeHtml(category.title)}</h2>
          ${categoryFaqs.map((faq) => accordionMarkup(faq, config.detailLinkText, true)).join('')}
        </div>
      `;
    }).join('');

    return `
      <faq-toc class="faq__toc hidden md:grid">${toc}</faq-toc>
      <div class="faq__content">${content}</div>
    `;
  };

  const updateStructuredData = (section, faqs) => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answerText,
        },
      })),
    };
    const script = document.createElement('script');

    script.type = 'application/ld+json';
    script.dataset.metaobjectFaqsStructuredData = '';
    script.textContent = JSON.stringify(structuredData);
    section.append(script);
  };

  const initializeSection = async (section) => {
    if (section.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
    section.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

    const configElement = section.querySelector('[data-metaobject-faqs-config]');
    const results = section.querySelector('[data-metaobject-faqs-results]');
    const status = section.querySelector('.faq__status');

    if (!configElement || !results || !status) return;

    try {
      const config = JSON.parse(configElement.textContent);
      status.textContent = config.loadingText;

      let faqs = (await fetchFaqs(config))
        .map((node) => normalizeMetaobject(node, config))
        .filter((faq) => faq.question && faq.answerHtml);

      if (config.productFilter) {
        faqs = faqs.filter((faq) => faq.productHandles.includes(config.productFilter));
      }

      faqs.sort((a, b) => a.sortOrder - b.sortOrder || a.question.localeCompare(b.question));

      if (!faqs.length) {
        status.textContent = config.emptyText;
        return;
      }

      results.innerHTML = renderFaqs(faqs, config);
      status.hidden = true;
      updateStructuredData(section, faqs);
    } catch (error) {
      status.textContent = JSON.parse(configElement.textContent).errorText;
      console.error('Unable to load FAQ metaobjects.', error);
    }
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(SECTION_SELECTOR)) initializeSection(scope);
    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(initializeSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
